import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { BACKEND } from "../utils/api";
import { useMusicPlayer } from "../context/MusicPlayerContext";
import {
  ChevronDown,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Repeat1,
  Heart,
  MoreHorizontal,
  Volume2,
  VolumeX,
  X,
  Clock,
  Share2,
  ListMusic,
  Mic2,
  Disc3,
  Info,
  Copy,
  Check,
  ListPlus,
  ExternalLink,
} from "lucide-react";

const GREEN = "#1DB954";

const AD_CONFIG = {
  SONGS_BEFORE_AD: 7,
  AD_DURATION: 15,
  PREMIUM_ROLES: ["premium", "admin", "creator"],
};

const SAMPLE_ADS = [
  {
    id: 1,
    imageUrl:
      "https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=1000&q=90",
    title: "Upgrade to Premium",
    description:
      "Enjoy ad-free music, offline downloads, and unlimited skips.",
    ctaText: "Get Premium",
    ctaLink: "/subscribe",
    advertiser: "Mint Music",
    brandColor: GREEN,
  },
  {
    id: 2,
    imageUrl:
      "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1000&q=90",
    title: "Discover New Artists",
    description:
      "Explore curated playlists from emerging artists worldwide.",
    ctaText: "Explore Now",
    ctaLink: "/explore",
    advertiser: "Mint Music",
    brandColor: "#7C3AED",
  },
  {
    id: 3,
    imageUrl:
      "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1000&q=90",
    title: "Share Your Music",
    description:
      "Upload your tracks and build your audience on Mint.",
    ctaText: "Start Creating",
    ctaLink: "/upload",
    advertiser: "Mint Music",
    brandColor: "#E11D48",
  },
];

export default function NowPlaying() {
  const {
    currentTrack,
    playing,
    togglePlay,
    nextTrack,
    prevTrack,
    progress,
    duration,
    seek,
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    favorites,
    addToFavorites,
    removeFromFavorites,
    setShowFull,

    // These are optional. If your context does not provide them,
    // they simply resolve to undefined.
    queue,
    playlist,
  } = useMusicPlayer();

  // ============================================================
  // STATE
  // ============================================================

  const [userSubscription, setUserSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] =
    useState(true);
  const [isPremium, setIsPremium] = useState(false);

  const [songsSinceLastAd, setSongsSinceLastAd] = useState(0);
  const [showAd, setShowAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(
    AD_CONFIG.AD_DURATION
  );
  const [currentAd, setCurrentAd] = useState(null);
  const [adSkippable, setAdSkippable] = useState(false);
  const [adProgress, setAdProgress] = useState(0);

  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);

  // Functional Spotify-style panels
  const [activePanel, setActivePanel] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [toast, setToast] = useState("");

  const [copied, setCopied] = useState(false);

  // ============================================================
  // REFS
  // ============================================================

  const touchStartY = useRef(null);
  const touchCurrentY = useRef(null);

  const adTimerRef = useRef(null);
  const prevTrackIdRef = useRef(null);
  const wasPlayingBeforeAd = useRef(false);
  const songsSinceLastAdRef = useRef(0);
  const adTriggeringRef = useRef(false);
  const toastTimerRef = useRef(null);

  // ============================================================
  // HELPERS
  // ============================================================

  const albumArt =
    currentTrack?.cover ||
    currentTrack?.thumbnail ||
    "https://picsum.photos/800";

  const formatTime = useCallback((seconds) => {
    if (!seconds || Number.isNaN(seconds)) {
      return "0:00";
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const formatAdTime = useCallback((seconds) => {
    if (seconds <= 0) return "0s";

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;
  }, []);

  const showToast = useCallback((message) => {
    setToast(message);

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = setTimeout(() => {
      setToast("");
      toastTimerRef.current = null;
    }, 2200);
  }, []);

  // ============================================================
  // SUBSCRIPTION
  // ============================================================

  const fetchUserSubscription = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("mintza_token") ||
        localStorage.getItem("token");

      if (!token) {
        setIsPremium(false);
        setIsLoadingSubscription(false);
        return;
      }

      const response = await fetch(`${BACKEND}/api/user/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setIsPremium(false);

          setUserSubscription({
            plan: "free",
            role: "user",
          });
        }

        return;
      }

      const data = await response.json();
      const user = data.user || data;

      const plan = user.subscription_plan || "free";
      const role = user.role || "user";
      const expires = user.subscription_expires || null;

      setUserSubscription({
        plan,
        role,
        expires,
      });

      const hasPremiumRole =
        AD_CONFIG.PREMIUM_ROLES.includes(
          role.toLowerCase()
        );

      const hasPremiumPlan =
        plan.toLowerCase() !== "free";

      const notExpired = expires
        ? new Date(expires) > new Date()
        : false;

      setIsPremium(
        hasPremiumRole ||
          (hasPremiumPlan && notExpired)
      );
    } catch (error) {
      console.error(
        "Failed to fetch subscription:",
        error
      );

      setIsPremium(false);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, []);

  // ============================================================
  // ADS
  // ============================================================

  const fetchAds = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("mintza_token") ||
        localStorage.getItem("token");

      const response = await fetch(
        `${BACKEND}/api/ads/music`,
        {
          method: "GET",
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.ads?.length) {
          return data.ads;
        }
      }
    } catch (error) {
      console.error("Failed to fetch ads:", error);
    }

    return null;
  }, []);

  const reportAdImpression = useCallback(
    async (adId) => {
      try {
        const token =
          localStorage.getItem("mintza_token") ||
          localStorage.getItem("token");

        if (!token) return;

        await fetch(
          `${BACKEND}/api/ads/impression`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              adId,
              placement: "music_player",
              trackId: currentTrack?.id,
            }),
          }
        );
      } catch (error) {
        console.error(
          "Failed to report ad impression:",
          error
        );
      }
    },
    [currentTrack?.id]
  );

  const reportAdClick = useCallback(
    async (adId) => {
      try {
        const token =
          localStorage.getItem("mintza_token") ||
          localStorage.getItem("token");

        if (!token) return;

        await fetch(`${BACKEND}/api/ads/click`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            adId,
            placement: "music_player",
            trackId: currentTrack?.id,
          }),
        });
      } catch (error) {
        console.error(
          "Failed to report ad click:",
          error
        );
      }
    },
    [currentTrack?.id]
  );

  const triggerAd = useCallback(async () => {
    if (adTriggeringRef.current || showAd) {
      return;
    }

    adTriggeringRef.current = true;

    wasPlayingBeforeAd.current = playing;

    if (playing) {
      togglePlay();
    }

    const backendAds = await fetchAds();

    const selectedAd =
      backendAds?.length > 0
        ? backendAds[
            Math.floor(
              Math.random() * backendAds.length
            )
          ]
        : SAMPLE_ADS[
            Math.floor(
              Math.random() * SAMPLE_ADS.length
            )
          ];

    setCurrentAd(selectedAd);
    setShowAd(true);

    reportAdImpression(selectedAd.id);

    adTriggeringRef.current = false;
  }, [
    showAd,
    playing,
    togglePlay,
    fetchAds,
    reportAdImpression,
  ]);

  const closeAd = useCallback(() => {
    if (adTimerRef.current) {
      clearInterval(adTimerRef.current);
      adTimerRef.current = null;
    }

    setShowAd(false);
    setCurrentAd(null);

    setAdCountdown(
      AD_CONFIG.AD_DURATION
    );

    setAdSkippable(false);
    setAdProgress(0);

    setSongsSinceLastAd(0);
    songsSinceLastAdRef.current = 0;

    if (wasPlayingBeforeAd.current) {
      togglePlay();
      wasPlayingBeforeAd.current = false;
    }
  }, [togglePlay]);

  // ============================================================
  // PLAYER CONTROLS
  // ============================================================

  const handleTogglePlay = useCallback(() => {
    if (showAd) return;
    togglePlay();
  }, [showAd, togglePlay]);

  const handleNextTrack = useCallback(() => {
    if (showAd) return;
    nextTrack();
  }, [showAd, nextTrack]);

  const handlePrevTrack = useCallback(() => {
    if (showAd) return;
    prevTrack();
  }, [showAd, prevTrack]);

  const handleSeek = useCallback(
    (value) => {
      if (showAd) return;
      seek(value);
    },
    [showAd, seek]
  );

  // ============================================================
  // FAVORITES
  // ============================================================

  const handleFavorite = useCallback(() => {
    if (!currentTrack?.id) return;

    const isFavorited = favorites.includes(
      currentTrack.id
    );

    if (isFavorited) {
      removeFromFavorites(currentTrack.id);
      showToast("Removed from Liked Songs");
    } else {
      addToFavorites(currentTrack.id);
      showToast("Added to Liked Songs");
    }
  }, [
    currentTrack?.id,
    favorites,
    addToFavorites,
    removeFromFavorites,
    showToast,
  ]);

  // ============================================================
  // QUEUE
  // ============================================================

  const getQueueItems = useCallback(() => {
    if (Array.isArray(queue) && queue.length) {
      return queue;
    }

    if (
      Array.isArray(playlist) &&
      playlist.length
    ) {
      return playlist;
    }

    return currentTrack ? [currentTrack] : [];
  }, [queue, playlist, currentTrack]);

  const openQueue = useCallback(() => {
    setShowMoreMenu(false);
    setActivePanel("queue");
  }, []);

  // ============================================================
  // LYRICS
  // ============================================================

  const getLyrics = useCallback(() => {
    if (!currentTrack) {
      return "";
    }

    return (
      currentTrack.lyrics ||
      currentTrack.lyric ||
      currentTrack.lyricsText ||
      currentTrack.description ||
      ""
    );
  }, [currentTrack]);

  const openLyrics = useCallback(() => {
    setShowMoreMenu(false);
    setActivePanel("lyrics");
  }, []);

  // ============================================================
  // TRACK DETAILS
  // ============================================================

  const openTrackInfo = useCallback(() => {
    setShowMoreMenu(false);
    setActivePanel("info");
  }, []);

  // ============================================================
  // SHARE
  // ============================================================

  const getShareUrl = useCallback(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const trackId = currentTrack?.id;

    if (trackId) {
      return `${window.location.origin}/track/${trackId}`;
    }

    return window.location.href;
  }, [currentTrack?.id]);

  const handleShare = useCallback(async () => {
    if (!currentTrack) return;

    setShowMoreMenu(false);

    const title =
      currentTrack.title ||
      "Untitled Track";

    const artist =
      currentTrack.artist ||
      "Unknown Artist";

    const shareUrl = getShareUrl();

    const shareData = {
      title: `${title} — ${artist}`,
      text: `Listen to ${title} by ${artist} on Mint Music`,
      url: shareUrl,
    };

    try {
      if (
        navigator.share &&
        window.isSecureContext
      ) {
        await navigator.share(shareData);
        showToast("Shared");
        return;
      }

      await navigator.clipboard.writeText(
        shareUrl
      );

      setCopied(true);
      showToast("Track link copied");

      setTimeout(() => {
        setCopied(false);
      }, 1600);
    } catch (error) {
      if (
        error?.name === "AbortError"
      ) {
        return;
      }

      try {
        const text = `${title} — ${artist}\n${shareUrl}`;

        if (
          navigator.clipboard &&
          window.isSecureContext
        ) {
          await navigator.clipboard.writeText(
            text
          );
        } else {
          const textarea =
            document.createElement("textarea");

          textarea.value = text;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";

          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          textarea.remove();
        }

        setCopied(true);
        showToast("Track link copied");

        setTimeout(() => {
          setCopied(false);
        }, 1600);
      } catch (copyError) {
        console.error(
          "Failed to share:",
          copyError
        );

        showToast("Unable to share track");
      }
    }
  }, [
    currentTrack,
    getShareUrl,
    showToast,
  ]);

  // ============================================================
  // MORE MENU
  // ============================================================

  const toggleMoreMenu = useCallback(() => {
    setShowMoreMenu((previous) => !previous);
  }, []);

  // ============================================================
  // SWIPE DOWN
  // ============================================================

  const handleTouchStart = useCallback(
    (event) => {
      if (showAd || activePanel || showMoreMenu) {
        return;
      }

      touchStartY.current =
        event.touches[0].clientY;

      touchCurrentY.current =
        event.touches[0].clientY;

      setIsDragging(false);
    },
    [showAd, activePanel, showMoreMenu]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (
        showAd ||
        activePanel ||
        showMoreMenu ||
        touchStartY.current === null
      ) {
        return;
      }

      const currentY =
        event.touches[0].clientY;

      touchCurrentY.current = currentY;

      const diff =
        currentY - touchStartY.current;

      if (diff > 8) {
        setIsDragging(true);

        setDragY(
          Math.min(180, diff * 0.42)
        );
      }
    },
    [showAd, activePanel, showMoreMenu]
  );

  const handleTouchEnd = useCallback(() => {
    if (
      showAd ||
      activePanel ||
      showMoreMenu
    ) {
      return;
    }

    if (
      isDragging &&
      touchStartY.current !== null &&
      touchCurrentY.current !== null
    ) {
      const diff =
        touchCurrentY.current -
        touchStartY.current;

      if (diff > 120) {
        setShowFull(false);
      }
    }

    setDragY(0);
    setIsDragging(false);

    touchStartY.current = null;
    touchCurrentY.current = null;
  }, [
    showAd,
    activePanel,
    showMoreMenu,
    isDragging,
    setShowFull,
  ]);

  // ============================================================
  // AD CTA
  // ============================================================

  const handleAdCtaClick = useCallback(() => {
    if (currentAd?.id) {
      reportAdClick(currentAd.id);
    }

    if (currentAd?.ctaLink) {
      closeAd();

      window.open(
        currentAd.ctaLink,
        "_blank",
        "noopener,noreferrer"
      );
    }
  }, [
    currentAd,
    closeAd,
    reportAdClick,
  ]);

  // ============================================================
  // MEDIA SESSION
  // ============================================================

  useEffect(() => {
    if (
      !currentTrack ||
      !("mediaSession" in navigator)
    ) {
      return;
    }

    navigator.mediaSession.metadata =
      new MediaMetadata({
        title:
          currentTrack.title ||
          "Unknown Title",

        artist:
          currentTrack.artist ||
          "Unknown Artist",

        album:
          currentTrack.album ||
          "Mint Music",

        artwork: [
          {
            src: albumArt,
            sizes: "512x512",
            type: "image/jpeg",
          },
        ],
      });

    const actions = [
      ["play", handleTogglePlay],
      ["pause", handleTogglePlay],
      ["previoustrack", handlePrevTrack],
      ["nexttrack", handleNextTrack],
    ];

    actions.forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(
          action,
          handler
        );
      } catch {
        // Unsupported
      }
    });

    try {
      navigator.mediaSession.setActionHandler(
        "seekto",
        (details) => {
          if (
            typeof details.seekTime ===
            "number"
          ) {
            seek(details.seekTime);
          }
        }
      );
    } catch {
      // Unsupported
    }

    return () => {
      actions.forEach(([action]) => {
        try {
          navigator.mediaSession.setActionHandler(
            action,
            null
          );
        } catch {
          // noop
        }
      });

      try {
        navigator.mediaSession.setActionHandler(
          "seekto",
          null
        );
      } catch {
        // noop
      }
    };
  }, [
    currentTrack,
    albumArt,
    handleTogglePlay,
    handleNextTrack,
    handlePrevTrack,
    seek,
  ]);

  useEffect(() => {
    if (
      "mediaSession" in navigator &&
      duration > 0
    ) {
      try {
        navigator.mediaSession.setPositionState({
          duration: Math.max(
            0.1,
            duration
          ),

          playbackRate: 1,

          position: Math.min(
            Math.max(0, progress),
            duration
          ),
        });
      } catch {
        // noop
      }
    }
  }, [progress, duration]);

  // ============================================================
  // SONG COUNT / ADS
  // ============================================================

  useEffect(() => {
    if (!currentTrack) return;

    if (
      prevTrackIdRef.current !== null &&
      prevTrackIdRef.current !==
        currentTrack.id
    ) {
      const newCount =
        songsSinceLastAdRef.current + 1;

      songsSinceLastAdRef.current =
        newCount;

      setSongsSinceLastAd(newCount);

      if (
        !isPremium &&
        !isLoadingSubscription &&
        newCount >=
          AD_CONFIG.SONGS_BEFORE_AD
      ) {
        triggerAd();
      }
    }

    prevTrackIdRef.current =
      currentTrack.id;
  }, [
    currentTrack?.id,
    isPremium,
    isLoadingSubscription,
    triggerAd,
  ]);

  useEffect(() => {
    fetchUserSubscription();
  }, [fetchUserSubscription]);

  // ============================================================
  // AD TIMER
  // ============================================================

  useEffect(() => {
    if (!showAd) return;

    setAdCountdown(
      AD_CONFIG.AD_DURATION
    );

    setAdSkippable(false);
    setAdProgress(0);

    adTimerRef.current = setInterval(() => {
      setAdCountdown((previous) => {
        const next = previous - 1;

        const progressValue =
          ((AD_CONFIG.AD_DURATION -
            next) /
            AD_CONFIG.AD_DURATION) *
          100;

        setAdProgress(
          Math.min(100, progressValue)
        );

        if (next <= 0) {
          clearInterval(
            adTimerRef.current
          );

          adTimerRef.current = null;

          closeAd();

          return 0;
        }

        if (next <= 5) {
          setAdSkippable(true);
        }

        return next;
      });
    }, 1000);

    return () => {
      if (adTimerRef.current) {
        clearInterval(
          adTimerRef.current
        );

        adTimerRef.current = null;
      }
    };
  }, [showAd, closeAd]);

  // ============================================================
  // BODY LOCK
  // ============================================================

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, []);

  // ============================================================
  // KEYBOARD
  // ============================================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (showMoreMenu) {
        setShowMoreMenu(false);
        return;
      }

      if (activePanel) {
        setActivePanel(null);
        return;
      }

      if (showAd && adSkippable) {
        closeAd();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    showMoreMenu,
    activePanel,
    showAd,
    adSkippable,
    closeAd,
  ]);

  // ============================================================
  // CLEANUP
  // ============================================================

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // ============================================================
  // RENDER GUARD
  // ============================================================

  if (!currentTrack) {
    return null;
  }

  const isFavorited = favorites.includes(
    currentTrack.id
  );

  const progressPercent =
    duration > 0
      ? Math.min(
          100,
          Math.max(
            0,
            (progress / duration) * 100
          )
        )
      : 0;

  const remainingTime =
    Math.max(0, duration - progress);

  const songsRemaining = Math.max(
    0,
    AD_CONFIG.SONGS_BEFORE_AD -
      songsSinceLastAd
  );

  const queueItems = getQueueItems();

  const lyrics = getLyrics();

  // ============================================================
  // AD SCREEN
  // ============================================================

  if (showAd && currentAd) {
    return (
      <div className="fixed inset-0 z-[100] overflow-hidden bg-black text-white select-none">
        <div className="absolute inset-0">
          <img
            src={currentAd.imageUrl}
            alt=""
            className="h-full w-full scale-125 object-cover blur-[90px] opacity-30"
          />

          <div className="absolute inset-0 bg-black/70" />

          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/65 to-black" />
        </div>

        <div className="absolute left-0 right-0 top-0 z-50 h-[3px] bg-white/10">
          <div
            className="h-full rounded-r-full transition-[width] duration-1000 linear"
            style={{
              width: `${adProgress}%`,
              backgroundColor:
                currentAd.brandColor ||
                GREEN,
            }}
          />
        </div>

        <header
          className="relative z-10 flex items-center justify-between px-5"
          style={{
            paddingTop:
              "max(1.25rem, env(safe-area-inset-top))",
          }}
        >
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  currentAd.brandColor ||
                  GREEN,
              }}
            />

            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
              {currentAd.advertiser ||
                "Sponsored"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur-xl">
              <Clock
                size={12}
                className="text-white/60"
              />

              <span className="font-mono text-xs">
                {formatAdTime(
                  adCountdown
                )}
              </span>
            </div>

            {adSkippable && (
              <button
                onClick={closeAd}
                className="flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-black transition active:scale-95"
              >
                <X size={13} />
                Skip
              </button>
            )}
          </div>
        </header>

        <main className="relative z-10 flex h-full flex-col items-center justify-center px-7 pb-20">
          <div className="w-full max-w-[370px]">
            <div className="aspect-square overflow-hidden rounded-2xl shadow-[0_30px_100px_rgba(0,0,0,0.7)]">
              <img
                src={currentAd.imageUrl}
                alt={currentAd.title}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="mt-8">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Sponsored
              </p>

              <h2 className="mt-2 text-3xl font-black tracking-tight">
                {currentAd.title}
              </h2>

              <p className="mt-3 text-[15px] leading-6 text-white/50">
                {currentAd.description}
              </p>
            </div>

            <button
              onClick={handleAdCtaClick}
              className="mt-7 w-full rounded-full py-4 text-sm font-bold transition hover:scale-[1.01] active:scale-[0.98]"
              style={{
                backgroundColor:
                  currentAd.brandColor ||
                  GREEN,

                color: "#000",
              }}
            >
              {currentAd.ctaText ||
                "Learn More"}
            </button>

            {!adSkippable && (
              <p className="mt-5 text-center text-[11px] text-white/25">
                Skip available in{" "}
                {adCountdown}s
              </p>
            )}
          </div>
        </main>

        <div
          className="absolute bottom-0 left-0 right-0 z-20 text-center"
          style={{
            paddingBottom:
              "max(1.5rem, env(safe-area-inset-bottom))",
          }}
        >
          <button
            onClick={handleAdCtaClick}
            className="text-[11px] font-medium text-white/30 underline underline-offset-4"
          >
            Go ad-free with Premium
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // NOW PLAYING
  // ============================================================

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden bg-black text-white select-none"
      style={{
        transform: `translateY(${dragY}px)`,
        transition: isDragging
          ? "none"
          : "transform 300ms cubic-bezier(.22,.61,.36,1)",
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ======================================================
          AMBIENT BACKGROUND
      ====================================================== */}

      <div className="absolute inset-0 overflow-hidden bg-black">
        <img
          src={albumArt}
          alt=""
          className="absolute inset-0 h-full w-full scale-[1.35] object-cover blur-[110px] opacity-45"
        />

        <div className="absolute inset-0 bg-black/55" />

        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/95" />

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_20%,rgba(0,0,0,.35)_75%,rgba(0,0,0,.8)_100%)]" />
      </div>

      {/* ======================================================
          PLAYER
      ====================================================== */}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[560px] flex-col px-5 sm:px-7">
        {/* ====================================================
            TOP BAR
        ==================================================== */}

        <header
          className="relative flex shrink-0 items-center justify-between"
          style={{
            paddingTop:
              "max(0.65rem, env(safe-area-inset-top))",
          }}
        >
          <button
            onClick={() => setShowFull(false)}
            aria-label="Close player"
            className="flex h-11 w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10 active:scale-90"
          >
            <ChevronDown
              size={28}
              strokeWidth={1.7}
            />
          </button>

          <div className="min-w-0 flex-1 px-8 text-center">
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/40">
              Playing from
            </p>

            <p className="mt-1 truncate text-xs font-semibold text-white/90">
              {currentTrack.album ||
                "Mint Music"}
            </p>
          </div>

          <div className="relative">
            <button
              onClick={toggleMoreMenu}
              aria-label="More options"
              aria-expanded={showMoreMenu}
              className="flex h-11 w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10 active:scale-90"
            >
              <MoreHorizontal
                size={25}
                strokeWidth={1.7}
              />
            </button>

            {/* ==================================================
                MORE MENU
            ================================================== */}

            {showMoreMenu && (
              <>
                <button
                  aria-label="Close menu"
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() =>
                    setShowMoreMenu(false)
                  }
                />

                <div className="absolute right-0 top-12 z-50 w-[230px] overflow-hidden rounded-2xl border border-white/10 bg-[#181818]/95 p-1.5 shadow-[0_20px_70px_rgba(0,0,0,.7)] backdrop-blur-2xl">
                  <button
                    onClick={handleFavorite}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-white transition hover:bg-white/10"
                  >
                    <Heart
                      size={18}
                      fill={
                        isFavorited
                          ? "currentColor"
                          : "none"
                      }
                      className={
                        isFavorited
                          ? "text-[#1DB954]"
                          : "text-white/70"
                      }
                    />

                    <span>
                      {isFavorited
                        ? "Remove from Liked Songs"
                        : "Add to Liked Songs"}
                    </span>
                  </button>

                  <button
                    onClick={handleShare}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-white transition hover:bg-white/10"
                  >
                    {copied ? (
                      <Check
                        size={18}
                        className="text-[#1DB954]"
                      />
                    ) : (
                      <Share2
                        size={18}
                        className="text-white/70"
                      />
                    )}

                    <span>
                      {copied
                        ? "Link copied"
                        : "Share"}
                    </span>
                  </button>

                  <button
                    onClick={openQueue}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-white transition hover:bg-white/10"
                  >
                    <ListMusic
                      size={18}
                      className="text-white/70"
                    />

                    <span>Open queue</span>
                  </button>

                  <button
                    onClick={openLyrics}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-white transition hover:bg-white/10"
                  >
                    <Mic2
                      size={18}
                      className="text-white/70"
                    />

                    <span>View lyrics</span>
                  </button>

                  <button
                    onClick={openTrackInfo}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-white transition hover:bg-white/10"
                  >
                    <Info
                      size={18}
                      className="text-white/70"
                    />

                    <span>Song details</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* ====================================================
            ALBUM ART
        ==================================================== */}

        <main className="flex min-h-0 flex-1 items-center justify-center py-5 sm:py-8">
          <div className="relative w-full max-w-[430px]">
            <div
              className="absolute inset-8 rounded-2xl opacity-40 blur-3xl"
              style={{
                backgroundImage: `url(${albumArt})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />

            <button
              type="button"
              onClick={openTrackInfo}
              aria-label="Open song details"
              className={`relative block w-full overflow-hidden rounded-[10px] shadow-[0_35px_90px_rgba(0,0,0,0.65)] transition-transform duration-700 ease-out ${
                playing
                  ? "scale-100"
                  : "scale-[0.985]"
              }`}
            >
              <div className="aspect-square">
                <img
                  src={albumArt}
                  alt={
                    currentTrack.title ||
                    "Album artwork"
                  }
                  draggable={false}
                  className="h-full w-full object-cover bg-neutral-900"
                />
              </div>
            </button>
          </div>
        </main>

        {/* ====================================================
            TRACK INFO
        ==================================================== */}

        <section className="shrink-0">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={openTrackInfo}
              className="min-w-0 flex-1 text-left"
              aria-label="Open track information"
            >
              <h1 className="truncate text-[23px] font-bold leading-tight tracking-[-0.03em]">
                {currentTrack.title ||
                  "Untitled Track"}
              </h1>

              <p className="mt-1 truncate text-[15px] font-medium text-white/55">
                {currentTrack.artist ||
                  "Unknown Artist"}
              </p>
            </button>

            <button
              onClick={handleFavorite}
              aria-label={
                isFavorited
                  ? "Remove from favorites"
                  : "Add to favorites"
              }
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition hover:bg-white/10 active:scale-90"
            >
              <Heart
                size={24}
                strokeWidth={1.8}
                className={
                  isFavorited
                    ? "text-[#1DB954]"
                    : "text-white/75"
                }
                fill={
                  isFavorited
                    ? "currentColor"
                    : "none"
                }
              />
            </button>
          </div>
        </section>

        {/* ====================================================
            PROGRESS
        ==================================================== */}

        <section className="mt-6 shrink-0">
          <div className="group relative flex h-4 items-center">
            <div className="absolute left-0 right-0 h-[4px] rounded-full bg-white/20" />

            <div
              className="absolute left-0 h-[4px] rounded-full bg-white transition-[width] duration-75"
              style={{
                width: `${progressPercent}%`,
              }}
            />

            <input
              aria-label="Track progress"
              type="range"
              min={0}
              max={duration || 100}
              step={0.1}
              value={Math.min(
                progress,
                duration || 100
              )}
              onChange={(event) =>
                handleSeek(
                  Number(event.target.value)
                )
              }
              className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
            />

            <div
              className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 rounded-full bg-white shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              style={{
                left: `${progressPercent}%`,
              }}
            />
          </div>

          <div className="mt-1 flex justify-between text-[10px] font-medium tabular-nums text-white/40">
            <span>
              {formatTime(progress)}
            </span>

            <span>
              -{formatTime(remainingTime)}
            </span>
          </div>
        </section>

        {/* ====================================================
            MAIN CONTROLS
        ==================================================== */}

        <section className="mt-5 flex shrink-0 items-center justify-between">
          <button
            onClick={() =>
              setShuffle(!shuffle)
            }
            aria-label={
              shuffle
                ? "Turn shuffle off"
                : "Turn shuffle on"
            }
            aria-pressed={shuffle}
            className={`flex h-11 w-11 items-center justify-center rounded-full transition active:scale-90 ${
              shuffle
                ? "text-[#1DB954]"
                : "text-white/65 hover:text-white"
            }`}
          >
            <Shuffle
              size={21}
              strokeWidth={
                shuffle ? 2.2 : 1.8
              }
            />
          </button>

          <button
            onClick={handlePrevTrack}
            aria-label="Previous track"
            className="flex h-12 w-12 items-center justify-center text-white transition active:scale-90"
          >
            <SkipBack
              size={31}
              fill="currentColor"
              strokeWidth={0}
            />
          </button>

          <button
            onClick={handleTogglePlay}
            aria-label={
              playing ? "Pause" : "Play"
            }
            className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white text-black shadow-[0_12px_40px_rgba(0,0,0,0.5)] transition hover:scale-[1.04] active:scale-95"
          >
            {playing ? (
              <Pause
                size={31}
                fill="currentColor"
                strokeWidth={0}
              />
            ) : (
              <Play
                size={32}
                fill="currentColor"
                strokeWidth={0}
                className="ml-1"
              />
            )}
          </button>

          <button
            onClick={handleNextTrack}
            aria-label="Next track"
            className="flex h-12 w-12 items-center justify-center text-white transition active:scale-90"
          >
            <SkipForward
              size={31}
              fill="currentColor"
              strokeWidth={0}
            />
          </button>

          <button
            onClick={() =>
              setRepeat(
                repeat === "none"
                  ? "all"
                  : repeat === "all"
                  ? "one"
                  : "none"
              )
            }
            aria-label={
              repeat === "none"
                ? "Turn repeat on"
                : repeat === "all"
                ? "Repeat all"
                : "Repeat one"
            }
            aria-pressed={repeat !== "none"}
            className={`flex h-11 w-11 items-center justify-center rounded-full transition active:scale-90 ${
              repeat !== "none"
                ? "text-[#1DB954]"
                : "text-white/65 hover:text-white"
            }`}
          >
            {repeat === "one" ? (
              <Repeat1
                size={21}
                strokeWidth={2}
              />
            ) : (
              <Repeat
                size={21}
                strokeWidth={
                  repeat === "all"
                    ? 2.1
                    : 1.8
                }
              />
            )}
          </button>
        </section>

        {/* ====================================================
            SECONDARY ACTIONS
            NO VOLUME CONTROL
        ==================================================== */}

        <section className="mt-5 flex shrink-0 items-center justify-between">
          {/* Queue */}
          <button
            onClick={openQueue}
            aria-label="Open queue"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white active:scale-90"
          >
            <ListMusic
              size={20}
              strokeWidth={1.7}
            />
          </button>

          {/* Lyrics */}
          <button
            onClick={openLyrics}
            aria-label="Open lyrics"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white active:scale-90"
          >
            <Mic2
              size={19}
              strokeWidth={1.7}
            />
          </button>

          {/* Track info */}
          <button
            onClick={openTrackInfo}
            aria-label="Track information"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white active:scale-90"
          >
            <Disc3
              size={19}
              strokeWidth={1.6}
            />
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            aria-label="Share track"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white active:scale-90"
          >
            {copied ? (
              <Check
                size={19}
                strokeWidth={2}
                className="text-[#1DB954]"
              />
            ) : (
              <Share2
                size={18}
                strokeWidth={1.7}
              />
            )}
          </button>
        </section>

        {/* ====================================================
            AD COUNTER
        ==================================================== */}

        {!isLoadingSubscription &&
          !isPremium && (
            <div className="pointer-events-none mt-3 text-center">
              <span className="text-[9px] font-medium tracking-wide text-white/20">
                {songsRemaining}{" "}
                {songsRemaining === 1
                  ? "song"
                  : "songs"}{" "}
                until ad
              </span>
            </div>
          )}

        <div
          className="shrink-0"
          style={{
            height:
              "max(0.8rem, env(safe-area-inset-bottom))",
          }}
        />
      </div>

      {/* ========================================================
          PANEL BACKDROP
      ======================================================== */}

      {activePanel && (
        <button
          aria-label="Close panel"
          onClick={() => setActivePanel(null)}
          className="absolute inset-0 z-[60] bg-black/50 backdrop-blur-[2px]"
        />
      )}

      {/* ========================================================
          QUEUE PANEL
      ======================================================== */}

      {activePanel === "queue" && (
        <section className="absolute bottom-0 left-0 right-0 z-[70] mx-auto max-h-[78vh] w-full max-w-[560px] overflow-hidden rounded-t-[28px] border-t border-white/10 bg-[#121212]/98 shadow-[0_-30px_100px_rgba(0,0,0,.65)] backdrop-blur-2xl">
          <div className="flex items-center justify-between px-5 pb-3 pt-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Up next
              </p>

              <h2 className="mt-1 text-xl font-bold">
                Queue
              </h2>
            </div>

            <button
              onClick={() =>
                setActivePanel(null)
              }
              aria-label="Close queue"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white active:scale-90"
            >
              <X size={20} />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto px-3 pb-8">
            {queueItems.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <ListMusic
                  size={38}
                  className="mx-auto text-white/20"
                />

                <p className="mt-4 text-sm text-white/45">
                  Your queue is empty
                </p>
              </div>
            ) : (
              queueItems.map(
                (track, index) => {
                  const isCurrent =
                    track?.id ===
                    currentTrack?.id;

                  const image =
                    track?.cover ||
                    track?.thumbnail ||
                    albumArt;

                  return (
                    <div
                      key={
                        track?.id ||
                        `${index}-${track?.title}`
                      }
                      className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${
                        isCurrent
                          ? "bg-white/[0.07]"
                          : "hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md bg-white/5">
                        <img
                          src={image}
                          alt=""
                          className="h-full w-full object-cover"
                        />

                        {isCurrent && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            {playing ? (
                              <div className="flex items-end gap-[2px]">
                                <span className="h-3 w-[3px] animate-pulse bg-[#1DB954]" />
                                <span className="h-5 w-[3px] animate-pulse bg-[#1DB954]" />
                                <span className="h-4 w-[3px] animate-pulse bg-[#1DB954]" />
                              </div>
                            ) : (
                              <Pause
                                size={17}
                                fill="currentColor"
                              />
                            )}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate text-sm font-medium ${
                            isCurrent
                              ? "text-[#1DB954]"
                              : "text-white"
                          }`}
                        >
                          {track?.title ||
                            "Untitled Track"}
                        </p>

                        <p className="mt-0.5 truncate text-xs text-white/40">
                          {track?.artist ||
                            "Unknown Artist"}
                        </p>
                      </div>

                      <span className="text-[10px] tabular-nums text-white/20">
                        {formatTime(
                          track?.duration || 0
                        )}
                      </span>
                    </div>
                  );
                }
              )
            )}
          </div>
        </section>
      )}

      {/* ========================================================
          LYRICS PANEL
      ======================================================== */}

      {activePanel === "lyrics" && (
        <section className="absolute bottom-0 left-0 right-0 z-[70] mx-auto max-h-[85vh] w-full max-w-[560px] overflow-hidden rounded-t-[28px] border-t border-white/10 bg-[#121212]/98 shadow-[0_-30px_100px_rgba(0,0,0,.65)] backdrop-blur-2xl">
          <div className="flex items-center justify-between px-5 pb-4 pt-5">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Lyrics
              </p>

              <h2 className="mt-1 truncate text-xl font-bold">
                {currentTrack.title ||
                  "Untitled Track"}
              </h2>
            </div>

            <button
              onClick={() =>
                setActivePanel(null)
              }
              aria-label="Close lyrics"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white active:scale-90"
            >
              <X size={20} />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-6 pb-12">
            {lyrics ? (
              <div className="whitespace-pre-wrap text-[21px] font-bold leading-[1.55] tracking-[-0.02em] text-white/90">
                {lyrics}
              </div>
            ) : (
              <div className="py-16 text-center">
                <Mic2
                  size={42}
                  className="mx-auto text-white/15"
                />

                <h3 className="mt-5 text-lg font-bold">
                  Lyrics aren't available
                </h3>

                <p className="mx-auto mt-2 max-w-[280px] text-sm leading-6 text-white/35">
                  Lyrics for this track haven't
                  been added yet.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========================================================
          TRACK INFO PANEL
      ======================================================== */}

      {activePanel === "info" && (
        <section className="absolute bottom-0 left-0 right-0 z-[70] mx-auto max-h-[82vh] w-full max-w-[560px] overflow-hidden rounded-t-[28px] border-t border-white/10 bg-[#121212]/98 shadow-[0_-30px_100px_rgba(0,0,0,.65)] backdrop-blur-2xl">
          <div className="flex items-center justify-between px-5 pb-4 pt-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Now playing
              </p>

              <h2 className="mt-1 text-xl font-bold">
                Song details
              </h2>
            </div>

            <button
              onClick={() =>
                setActivePanel(null)
              }
              aria-label="Close song details"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white active:scale-90"
            >
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto px-5 pb-10">
            <div className="mx-auto aspect-square max-w-[300px] overflow-hidden rounded-2xl shadow-[0_25px_70px_rgba(0,0,0,.6)]">
              <img
                src={albumArt}
                alt={
                  currentTrack.title ||
                  "Album artwork"
                }
                className="h-full w-full object-cover"
              />
            </div>

            <div className="mt-6">
              <h3 className="text-2xl font-bold tracking-tight">
                {currentTrack.title ||
                  "Untitled Track"}
              </h3>

              <p className="mt-1 text-base text-white/50">
                {currentTrack.artist ||
                  "Unknown Artist"}
              </p>
            </div>

            <div className="mt-6 divide-y divide-white/[0.07] rounded-2xl bg-white/[0.035]">
              <div className="flex items-center justify-between px-4 py-4">
                <span className="text-sm text-white/40">
                  Album
                </span>

                <span className="max-w-[60%] truncate text-right text-sm font-medium">
                  {currentTrack.album ||
                    "Mint Music"}
                </span>
              </div>

              {currentTrack.genre && (
                <div className="flex items-center justify-between px-4 py-4">
                  <span className="text-sm text-white/40">
                    Genre
                  </span>

                  <span className="text-sm font-medium">
                    {currentTrack.genre}
                  </span>
                </div>
              )}

              {currentTrack.releaseDate && (
                <div className="flex items-center justify-between px-4 py-4">
                  <span className="text-sm text-white/40">
                    Released
                  </span>

                  <span className="text-sm font-medium">
                    {currentTrack.releaseDate}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between px-4 py-4">
                <span className="text-sm text-white/40">
                  Duration
                </span>

                <span className="text-sm font-medium">
                  {formatTime(duration)}
                </span>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={handleFavorite}
                className="flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-3 text-sm font-semibold transition hover:bg-white/15 active:scale-[0.98]"
              >
                <Heart
                  size={17}
                  fill={
                    isFavorited
                      ? "currentColor"
                      : "none"
                  }
                  className={
                    isFavorited
                      ? "text-[#1DB954]"
                      : ""
                  }
                />

                {isFavorited
                  ? "Liked"
                  : "Like"}
              </button>

              <button
                onClick={handleShare}
                className="flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-3 text-sm font-semibold transition hover:bg-white/15 active:scale-[0.98]"
              >
                {copied ? (
                  <Check
                    size={17}
                    className="text-[#1DB954]"
                  />
                ) : (
                  <Share2 size={17} />
                )}

                {copied
                  ? "Copied"
                  : "Share"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ========================================================
          TOAST
      ======================================================== */}

      {toast && (
        <div className="pointer-events-none absolute bottom-7 left-1/2 z-[100] -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-black shadow-[0_15px_50px_rgba(0,0,0,.5)]">
            <Check
              size={14}
              strokeWidth={2.5}
            />

            {toast}
          </div>
        </div>
      )}

      {/* ========================================================
          RANGE INPUT STYLING
      ======================================================== */}

      <style>{`
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
        }

        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 0;
          height: 0;
          border: 0;
          background: transparent;
        }

        input[type="range"]::-moz-range-thumb {
          width: 0;
          height: 0;
          border: 0;
          background: transparent;
        }

        input[type="range"]::-webkit-slider-runnable-track {
          background: transparent;
          height: 4px;
        }

        input[type="range"]::-moz-range-track {
          background: transparent;
          border: 0;
          height: 4px;
        }

        input[type="range"]:focus {
          outline: none;
        }

        button {
          -webkit-tap-highlight-color: transparent;
        }

        * {
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,.15) transparent;
        }
      `}</style>
    </div>
  );
}
